import { createContext, useContext, useLayoutEffect, useState, ReactNode } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Get theme from localStorage or default to light
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme')
      return (saved as Theme) || 'light'
    }
    return 'light'
  })

  // Keep DOM (html class + color-scheme) in sync with state.
  // useLayoutEffect avoids “stuck” styles/flash on toggle.
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return

    localStorage.setItem('theme', theme)
    const root = document.documentElement

    if (theme === 'dark') {
      root.classList.add('dark')
      root.style.colorScheme = 'dark'
    } else {
      root.classList.remove('dark')
      root.style.colorScheme = 'light'
    }
  }, [theme])

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light')
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
